/**
 * Outil « États de la matière » (Boîte à outils, physique-chimie 6e → 2nde) — logique PURE.
 *
 * Trois parties :
 * - États : on chauffe (ou on refroidit) 100 g d'un corps pur ou d'un mélange à puissance
 *   constante ; courbe de température, paliers des corps purs (pas de palier pour l'eau salée),
 *   proportions solide / liquide / gaz, masse conservée, volume qui change ; en 2nde, énergie
 *   échangée Q = m × L, transformations endothermiques et exothermiques.
 * - Masse volumique : un cube d'un matériau dans un liquide (flotte ou coule), m = ρ × V,
 *   conversions g/cm³, g/L, kg/m³.
 * - Dissolution : du sel ou du sucre dans 100 mL d'eau, jusqu'à saturation (solubilité selon la
 *   température) ; masse conservée ; en 2nde, concentration en masse.
 *
 * Données : valeurs usuelles des manuels, sous pression atmosphérique normale.
 */

import { frNum, mcqOptions, pick, retryMcq, shuffle, type Mcq, type Rng } from '../../ui/mcq';

export const fr = frNum;
const NB = String.fromCharCode(0xa0);

export type Part = 'states' | 'density' | 'dissolve';

export const PARTS: ReadonlyArray<{ id: Part; label: string }> = [
  { id: 'states', label: 'États' },
  { id: 'density', label: 'Masse volumique' },
  { id: 'dissolve', label: 'Dissolution' },
];

/** la dissolution : 6e, 5e (saturation, solubilité) et 2nde (concentration en masse) */
export function partsFor(rank: number | null) {
  return PARTS.filter((p) => p.id !== 'dissolve' || rank === null || rank <= 2 || rank >= 5);
}

/** 5e et après : modèle particulaire */
export const particulate = (rank: number | null) => rank === null || rank >= 2;
/** 2nde : énergie de changement d'état */
export const energetic = (rank: number | null) => rank === null || rank >= 5;

/* ---------- changements d'état ---------- */

export type Phase = 'solid' | 'liquid' | 'gas';

export interface Substance {
  id: string;
  label: string;
  formula: string;
  pure: boolean;
  /** g/mol (volume du gaz) */
  M: number;
  /** températures de fusion et d'ébullition (°C) ; null : hors de l'expérience */
  Tf: number;
  Teb: number | null;
  /** capacités thermiques massiques J/(g·K) */
  cs: number;
  cl: number;
  cg: number;
  /** énergies massiques de fusion et de vaporisation J/g */
  Lf: number;
  Lv: number;
  /** masses volumiques g/mL */
  rhoS: number;
  rhoL: number;
  /** températures de début et de fin (°C) */
  range: readonly [number, number];
  /** puissance du chauffage (ou du refroidissement), W */
  power: number;
  /** sens habituel de l'expérience */
  usual: 'heat' | 'cool';
}

export const MASS = 100; // g

export const SUBSTANCES: readonly Substance[] = [
  {
    id: 'water',
    label: 'Eau',
    formula: 'H₂O',
    pure: true,
    M: 18,
    Tf: 0,
    Teb: 100,
    cs: 2.1,
    cl: 4.18,
    cg: 2.0,
    Lf: 334,
    Lv: 2257,
    rhoS: 0.917,
    rhoL: 1.0,
    range: [-20, 120],
    power: 500,
    usual: 'heat',
  },
  {
    id: 'cyclohexane',
    label: 'Cyclohexane',
    formula: 'C₆H₁₂',
    pure: true,
    M: 84,
    Tf: 6.5,
    Teb: 81,
    cs: 1.6,
    cl: 1.85,
    cg: 1.3,
    Lf: 31,
    Lv: 358,
    rhoS: 0.85,
    rhoL: 0.78,
    range: [-20, 100],
    power: 100,
    usual: 'heat',
  },
  {
    id: 'stearic',
    label: 'Acide stéarique',
    formula: 'C₁₈H₃₆O₂',
    pure: true,
    M: 284,
    Tf: 69,
    Teb: null,
    cs: 1.6,
    cl: 2.3,
    cg: 0,
    Lf: 199,
    Lv: 0,
    rhoS: 0.94,
    rhoL: 0.85,
    range: [20, 100],
    power: 60,
    usual: 'cool',
  },
  {
    id: 'brine',
    label: 'Eau salée',
    formula: 'H₂O + NaCl',
    pure: false,
    M: 18,
    // 10 g de sel dans 90 g d'eau : la glace commence à se former vers −6,6 °C
    Tf: -6.6,
    Teb: null,
    cs: 3.7,
    cl: 3.7,
    cg: 0,
    Lf: 334,
    Lv: 0,
    rhoS: 0.95,
    rhoL: 1.07,
    range: [-18, 20],
    power: 60,
    usual: 'cool',
  },
];

export const substanceById = (id: string) => SUBSTANCES.find((s) => s.id === id)!;

/** eau salée : eau gelée (g) à la température T, à l'équilibre (abaissement du point de fusion) */
const BRINE_WATER = 90;
const BRINE_K = BRINE_WATER * 6.6; // −T × (eau liquide restante) = constante
export function brineIce(T: number): number {
  if (T >= -6.6) return 0;
  return Math.max(0, BRINE_WATER - BRINE_K / -T);
}

export interface MatterState {
  T: number;
  /** proportions en masse */
  solid: number;
  liquid: number;
  gas: number;
}

interface Seg {
  dh: number;
  at: (u: number) => MatterState; // u de 0 à 1 dans le segment
}

function segmentsOf(s: Substance): Seg[] {
  const [t0, t1] = s.range;
  const m = MASS;
  const st = (T: number, solid: number, liquid: number, gas: number): MatterState => ({
    T,
    solid,
    liquid,
    gas,
  });
  const out: Seg[] = [];
  out.push({ dh: m * s.cs * (s.Tf - t0), at: (u) => st(t0 + u * (s.Tf - t0), 1, 0, 0) });
  out.push({ dh: m * s.Lf, at: (u) => st(s.Tf, 1 - u, u, 0) });
  const top = s.Teb ?? t1;
  out.push({ dh: m * s.cl * (top - s.Tf), at: (u) => st(s.Tf + u * (top - s.Tf), 0, 1, 0) });
  if (s.Teb !== null) {
    out.push({ dh: m * s.Lv, at: (u) => st(s.Teb!, 0, 1 - u, u) });
    out.push({ dh: m * s.cg * (t1 - s.Teb), at: (u) => st(s.Teb! + u * (t1 - s.Teb!), 0, 0, 1) });
  }
  return out;
}

/** énergie (J) reçue pour aller du début (froid) à la fin (chaud) de l'expérience */
export function totalEnergy(s: Substance): number {
  if (!s.pure) return brineH(s, s.range[1]);
  return segmentsOf(s).reduce((a, g) => a + g.dh, 0);
}

/** eau salée : énergie reçue depuis le début (froid) jusqu'à T */
function brineH(s: Substance, T: number): number {
  const t0 = s.range[0];
  return MASS * s.cl * (T - t0) + s.Lf * (brineIce(t0) - brineIce(T));
}

/** état après avoir reçu l'énergie h (J) depuis le début froid de l'expérience */
export function stateAtEnergy(s: Substance, h: number): MatterState {
  const H = totalEnergy(s);
  const e = Math.min(H, Math.max(0, h));
  if (!s.pure) {
    let lo = s.range[0];
    let hi = s.range[1];
    for (let i = 0; i < 60; i++) {
      const mid = (lo + hi) / 2;
      if (brineH(s, mid) < e) lo = mid;
      else hi = mid;
    }
    const T = (lo + hi) / 2;
    const ice = brineIce(T) / MASS;
    return { T, solid: ice, liquid: 1 - ice, gas: 0 };
  }
  let acc = 0;
  const segs = segmentsOf(s);
  for (const g of segs) {
    if (e <= acc + g.dh + 1e-9 && g.dh > 0) return g.at((e - acc) / g.dh);
    acc += g.dh;
  }
  return segs[segs.length - 1]!.at(1);
}

export type Direction = 'heat' | 'cool';

/** durée de l'expérience (s) */
export const duration = (s: Substance) => totalEnergy(s) / s.power;

/** état à l'instant t (s) ; en refroidissant, on part du chaud */
export function stateAt(s: Substance, dir: Direction, t: number): MatterState {
  const q = s.power * t;
  return stateAtEnergy(s, dir === 'heat' ? q : totalEnergy(s) - q);
}

/** courbe θ(t), n + 1 points */
export function curve(s: Substance, dir: Direction, n = 240): Array<[number, number]> {
  const D = duration(s);
  return Array.from({ length: n + 1 }, (_, k) => {
    const t = (k / n) * D;
    return [t, stateAt(s, dir, t).T];
  });
}

/** volume (mL) : solide et liquide par leur masse volumique, gaz parfait sous 1 013 hPa */
export function volumeOf(s: Substance, st: MatterState): number {
  const vs = (MASS * st.solid) / s.rhoS;
  const vl = (MASS * st.liquid) / s.rhoL;
  const vg = st.gas > 0 ? ((((MASS * st.gas) / s.M) * 8.314 * (st.T + 273.15)) / 101325) * 1e6 : 0;
  return vs + vl + vg;
}

export function volumeText(v: number): string {
  return v >= 1000 ? `${fr(v / 1000, v >= 10000 ? 0 : 1)}${NB}L` : `${fr(v, 0)}${NB}mL`;
}

/** nombres de particules dessinées dans chaque état (total n) */
export function phaseCounts(st: MatterState, n: number): Record<Phase, number> {
  const solid = Math.round(st.solid * n);
  const gas = Math.round(st.gas * n);
  return { solid, liquid: Math.max(0, n - solid - gas), gas };
}

/** changement d'état en cours (ou null) */
export function changeOf(s: Substance, st: MatterState, dir: Direction): string | null {
  const heat = dir === 'heat';
  if (st.solid > 1e-6 && st.liquid > 1e-6) return heat ? 'fusion' : 'solidification';
  if (st.liquid > 1e-6 && st.gas > 1e-6) return heat ? 'vaporisation' : 'liquéfaction';
  return null;
}

export const PHASE_NAMES: Record<Phase, string> = {
  solid: 'solide',
  liquid: 'liquide',
  gas: 'gaz',
};

export function stateText(st: MatterState): string {
  const parts = (['solid', 'liquid', 'gas'] as const).filter((p) => st[p] > 1e-6);
  if (parts.length === 1) return PHASE_NAMES[parts[0]!];
  return parts.map((p) => `${PHASE_NAMES[p]} ${fr(100 * st[p], 0)}${NB}%`).join(' + ');
}

export const tempText = (T: number) => `${fr(T, 1)}${NB}°C`;

export function minutes(t: number): string {
  const m = Math.floor(t / 60);
  const s = Math.round(t - 60 * m);
  return s === 60 ? `${m + 1}${NB}min` : m === 0 ? `${s}${NB}s` : `${m}${NB}min${NB}${s}${NB}s`;
}

const SYM: Record<Phase, string> = { solid: 's', liquid: 'ℓ', gas: 'g' };

export function stateLines(s: Substance, dir: Direction, t: number, rank: number | null): string[] {
  const st = stateAt(s, dir, t);
  const ch = changeOf(s, st, dir);
  const out: string[] = [];
  if (ch && s.pure) {
    out.push(
      `${ch[0]!.toUpperCase()}${ch.slice(1)} : palier à ${tempText(st.T)}. Pour un corps pur, la température reste constante tant que les deux états coexistent.`,
    );
  } else if (ch) {
    out.push(
      `${ch[0]!.toUpperCase()}${ch.slice(1)} à ${tempText(st.T)} : pas de palier, la température continue ${dir === 'heat' ? 'de monter' : 'de baisser'}. C’est un mélange, pas un corps pur.`,
    );
  } else {
    out.push(
      `${s.label} ${stateText(st)} à ${tempText(st.T)} : la température ${dir === 'heat' ? 'monte' : 'baisse'}.`,
    );
  }
  out.push(
    `La masse se conserve (${MASS}${NB}g) ; le volume change : ${volumeText(volumeOf(s, st))}.`,
  );
  if (particulate(rank)) {
    const during: Record<string, string> = {
      fusion:
        'Fusion : les particules quittent leur arrangement ordonné ; elles restent rapprochées mais deviennent désordonnées.',
      solidification:
        'Solidification : les particules, toujours rapprochées, s’arrangent en ordre.',
      vaporisation:
        'Vaporisation : des particules s’échappent du liquide et s’éloignent les unes des autres.',
      liquéfaction: 'Liquéfaction : les particules du gaz se rapprochent et forment le liquide.',
    };
    out.push(
      ch !== null
        ? during[ch]!
        : st.gas > 0.5
          ? 'Gaz : particules éloignées, en mouvement désordonné rapide.'
          : st.solid > 0.5
            ? 'Solide : particules rapprochées et ordonnées, qui vibrent sur place.'
            : 'Liquide : particules rapprochées mais désordonnées, qui glissent les unes sur les autres.',
    );
  }
  if (energetic(rank)) {
    const q = s.power * t;
    out.push(
      `Énergie ${dir === 'heat' ? 'reçue' : 'cédée'} depuis le début : Q = P × t = ${fr(q / 1000, 1)}${NB}kJ.`,
    );
    if (ch === 'fusion' || ch === 'solidification') {
      const eq =
        ch === 'fusion'
          ? `${s.formula}(${SYM.solid}) → ${s.formula}(${SYM.liquid})`
          : `${s.formula}(${SYM.liquid}) → ${s.formula}(${SYM.solid})`;
      out.push(
        `${s.pure ? eq + ' : ' : ''}${ch === 'fusion' ? 'endothermique (le système reçoit de l’énergie)' : 'exothermique (le système cède de l’énergie)'} ; Q = m × L_fus = ${MASS}${NB}g × ${fr(s.Lf, 0)}${NB}J/g = ${fr((MASS * s.Lf) / 1000, 1)}${NB}kJ.`,
      );
    }
    if (ch === 'vaporisation' || ch === 'liquéfaction') {
      const eq =
        ch === 'vaporisation'
          ? `${s.formula}(${SYM.liquid}) → ${s.formula}(${SYM.gas})`
          : `${s.formula}(${SYM.gas}) → ${s.formula}(${SYM.liquid})`;
      out.push(
        `${eq} : ${ch === 'vaporisation' ? 'endothermique' : 'exothermique'} ; Q = m × L_vap = ${MASS}${NB}g × ${fr(s.Lv, 0)}${NB}J/g = ${fr((MASS * s.Lv) / 1000, 1)}${NB}kJ.`,
      );
    }
  }
  return out;
}

/* ---------- masse volumique ---------- */

export interface Material {
  id: string;
  label: string;
  /** nom dans une phrase (« aluminium », « PVC ») et avec l'article (« l’aluminium ») */
  name: string;
  the: string;
  /** g/cm³ */
  rho: number;
  color: string;
}

export const MATERIALS: readonly Material[] = [
  { id: 'cork', label: 'Liège', name: 'liège', the: 'le liège', rho: 0.24, color: '#c08a52' },
  { id: 'oak', label: 'Chêne', name: 'chêne', the: 'le chêne', rho: 0.75, color: '#a16207' },
  { id: 'ice', label: 'Glace', name: 'glace', the: 'la glace', rho: 0.92, color: '#bae6fd' },
  { id: 'pvc', label: 'PVC', name: 'PVC', the: 'le PVC', rho: 1.38, color: '#94a3b8' },
  {
    id: 'alu',
    label: 'Aluminium',
    name: 'aluminium',
    the: 'l’aluminium',
    rho: 2.7,
    color: '#cbd5e1',
  },
  { id: 'iron', label: 'Fer', name: 'fer', the: 'le fer', rho: 7.87, color: '#64748b' },
  { id: 'copper', label: 'Cuivre', name: 'cuivre', the: 'le cuivre', rho: 8.96, color: '#c2410c' },
  { id: 'lead', label: 'Plomb', name: 'plomb', the: 'le plomb', rho: 11.35, color: '#475569' },
  { id: 'gold', label: 'Or', name: 'or', the: 'l’or', rho: 19.3, color: '#eab308' },
];

export const LIQUIDS: readonly Material[] = [
  { id: 'water', label: 'Eau', name: 'eau', the: 'l’eau', rho: 1.0, color: '#60a5fa' },
  { id: 'oil', label: 'Huile', name: 'huile', the: 'l’huile', rho: 0.92, color: '#facc15' },
  {
    id: 'ethanol',
    label: 'Éthanol',
    name: 'éthanol',
    the: 'l’éthanol',
    rho: 0.79,
    color: '#c4b5fd',
  },
  {
    id: 'salt',
    label: 'Eau salée',
    name: 'eau salée',
    the: 'l’eau salée',
    rho: 1.2,
    color: '#5eead4',
  },
];

/** « de fer », « d’aluminium » */
export const de = (name: string) => (/^[aeiouyéèêh]/i.test(name) ? `d’${name}` : `de ${name}`);

export type Buoyancy = 'floats' | 'sinks' | 'between';

export function buoyancy(rhoObj: number, rhoLiq: number): Buoyancy {
  if (Math.abs(rhoObj - rhoLiq) < 1e-9) return 'between';
  return rhoObj < rhoLiq ? 'floats' : 'sinks';
}

/** part immergée d'un objet qui flotte : ρ_objet ÷ ρ_liquide */
export const immersed = (rhoObj: number, rhoLiq: number) => Math.min(1, rhoObj / rhoLiq);

export const rhoText = (rho: number) => `${fr(rho, 2)}${NB}g/cm³`;

export function densityLines(
  mat: Material,
  liq: Material,
  V: number,
  rank: number | null,
): string[] {
  const m = mat.rho * V;
  const b = buoyancy(mat.rho, liq.rho);
  const out = [
    `m = ρ × V = ${fr(mat.rho, 2)}${NB}g/cm³ × ${fr(V, 0)}${NB}cm³ = ${fr(m, 1)}${NB}g.`,
  ];
  if (rank !== null && rank <= 1)
    out[0] = `${mat.label} : ${fr(V, 0)}${NB}cm³ ont une masse de ${fr(m, 1)}${NB}g ; même volume d’eau : ${fr(V, 1)}${NB}g. La masse est proportionnelle au volume.`;
  out.push(
    b === 'floats'
      ? `ρ(${mat.name}) < ρ(${liq.name}) : le cube flotte, ${fr(100 * immersed(mat.rho, liq.rho), 0)}${NB}% de son volume sous la surface.`
      : b === 'sinks'
        ? `ρ(${mat.name}) > ρ(${liq.name}) : le cube coule, quel que soit son volume.`
        : 'Masses volumiques égales : le cube reste entre deux eaux.',
  );
  if (rank === null || rank >= 3)
    out.push(
      `${fr(mat.rho, 2)}${NB}g/cm³ = ${fr(mat.rho * 1000, 0)}${NB}g/L = ${fr(mat.rho * 1000, 0)}${NB}kg/m³ (1${NB}cm³ = 1${NB}mL ; 1${NB}m³ = 1${NB}000${NB}L).`,
    );
  return out;
}

/* ---------- dissolution ---------- */

export interface Solute {
  id: string;
  label: string;
  /** solubilité en g pour 100 mL d'eau, selon la température (°C) */
  table: ReadonlyArray<readonly [number, number]>;
  step: number;
  max: number;
  /** masse volumique du solide (g/mL) : volume ajouté à la solution, à peu près */
  rho: number;
}

export const SOLUTES: readonly Solute[] = [
  {
    id: 'salt',
    label: 'Sel',
    table: [
      [0, 35.7],
      [20, 36],
      [60, 37.1],
      [100, 39.2],
    ],
    step: 4,
    max: 60,
    rho: 2.16,
  },
  {
    id: 'sugar',
    label: 'Sucre',
    table: [
      [0, 179],
      [20, 204],
      [60, 287],
      [100, 487],
    ],
    step: 40,
    max: 520,
    rho: 1.59,
  },
];

export const WATER_ML = 100;

export function solubility(s: Solute, T: number): number {
  const tb = s.table;
  for (let i = 0; i < tb.length - 1; i++) {
    const [a, sa] = tb[i]!;
    const [b, sb] = tb[i + 1]!;
    if (T <= b) return sa + ((Math.max(T, a) - a) / (b - a)) * (sb - sa);
  }
  return tb[tb.length - 1]![1];
}

export function dissolve(s: Solute, added: number, T: number) {
  const sol = solubility(s, T);
  const dissolved = Math.min(added, sol);
  /** volume de la solution (mL) : l'eau plus, à peu près, le volume du solide dissous */
  const volume = WATER_ML + dissolved / s.rho;
  return {
    solubility: sol,
    dissolved,
    deposit: added - dissolved,
    saturated: added >= sol - 1e-9,
    volume,
    /** concentration en masse, g/L */
    cm: dissolved / (volume / 1000),
  };
}

export function dissolveLines(s: Solute, added: number, T: number, rank: number | null): string[] {
  const d = dissolve(s, added, T);
  const name = s.label.toLowerCase();
  const out: string[] = [];
  if (added === 0)
    out.push(`Ajoute du ${name} dans ${WATER_ML}${NB}mL d’eau à ${fr(T, 0)}${NB}°C.`);
  else if (!d.saturated)
    out.push(`Tout le ${name} est dissous : solution non saturée (on peut encore en dissoudre).`);
  else
    out.push(
      `Solution saturée : au-delà de ${fr(d.solubility, 0)}${NB}g pour ${WATER_ML}${NB}mL à ${fr(T, 0)}${NB}°C, le ${name} ne se dissout plus${d.deposit > 0.05 ? ` : ${fr(d.deposit, 0)}${NB}g restent au fond` : ''}.`,
    );
  out.push(
    `La masse se conserve : ${WATER_ML}${NB}g d’eau + ${fr(added, 0)}${NB}g de ${name} = ${fr(WATER_ML + added, 0)}${NB}g, même si on ne voit plus le ${name} dissous.`,
  );
  out.push(
    s.id === 'sugar'
      ? 'Chauffer l’eau permet de dissoudre beaucoup plus de sucre.'
      : 'La solubilité du sel change peu avec la température.',
  );
  if (rank === null || rank >= 5)
    out.push(
      `Concentration en masse : C_m = m(dissous) ÷ V(solution) = ${fr(d.dissolved, 1)}${NB}g ÷ ${fr(d.volume / 1000, 3)}${NB}L ≈ ${fr(d.cm, 0)}${NB}g/L (la solution occupe un peu plus que les ${WATER_ML}${NB}mL d’eau).`,
    );
  return out;
}

/* ---------- défi ---------- */

export type QuizKind =
  | 'state-name'
  | 'plateau'
  | 'mass-conserved'
  | 'same-volume'
  | 'dissolve-mass'
  | 'particles'
  | 'pure-mixture'
  | 'volume-change'
  | 'saturation'
  | 'rho-calc'
  | 'mass-calc'
  | 'units'
  | 'floats'
  | 'identify'
  | 'latent-heat'
  | 'endo-exo'
  | 'identify-temp'
  | 'mass-concentration';

export type Quiz = Mcq<QuizKind>;

export function quizKinds(rank: number | null): QuizKind[] {
  if (rank === null) return ['state-name', 'particles', 'rho-calc', 'floats', 'latent-heat'];
  if (rank <= 1) return ['state-name', 'plateau', 'mass-conserved', 'same-volume', 'dissolve-mass'];
  if (rank === 2) return ['particles', 'pure-mixture', 'volume-change', 'saturation', 'plateau'];
  if (rank <= 4) return ['rho-calc', 'mass-calc', 'units', 'floats', 'identify'];
  return ['latent-heat', 'endo-exo', 'identify-temp', 'mass-concentration', 'rho-calc'];
}

export function makeQuiz(kind: QuizKind, rng: Rng): Quiz {
  return retryMcq((r) => buildQuiz(kind, r), rng);
}

type W = readonly [string, string];
const q = (
  kind: QuizKind,
  question: string,
  good: string,
  wrongs: W[],
  explain: string,
  rng: Rng,
): Quiz => ({ kind, question, options: mcqOptions(good, wrongs, rng), explain });
const g = (v: number, d = 1) => `${fr(v, d)}${NB}g`;

const CHANGES: ReadonlyArray<{ name: string; from: string; to: string }> = [
  { name: 'La fusion', from: 'solide', to: 'liquide' },
  { name: 'La solidification', from: 'liquide', to: 'solide' },
  { name: 'La vaporisation', from: 'liquide', to: 'gazeux' },
  { name: 'La liquéfaction', from: 'gazeux', to: 'liquide' },
];

const PARTICLE_STATES: ReadonlyArray<{ state: string; text: string }> = [
  { state: 'un solide', text: 'Rapprochées et ordonnées ; elles vibrent sur place' },
  {
    state: 'un liquide',
    text: 'Rapprochées mais désordonnées ; elles glissent les unes sur les autres',
  },
  { state: 'un gaz', text: 'Éloignées et désordonnées ; elles bougent vite dans tout l’espace' },
];

function buildQuiz(kind: QuizKind, rng: Rng): Quiz {
  switch (kind) {
    case 'state-name': {
      const c = pick(CHANGES, rng);
      return q(
        kind,
        `Comment s’appelle le passage de l’état ${c.from} à l’état ${c.to} ?`,
        c.name,
        CHANGES.filter((x) => x !== c).map(
          (x) =>
            [x.name, `${x.name}, c’est le passage de l’état ${x.from} à l’état ${x.to}.`] as const,
        ),
        `${c.name} : état ${c.from} → état ${c.to}.`,
        rng,
      );
    }
    case 'plateau': {
      const boil = rng() < 0.5;
      const [what, T, other] = boil ? ['l’ébullition', 100, 0] : ['la fusion de la glace', 0, 100];
      return q(
        kind,
        `On chauffe de l’eau pure. Pendant ${what}, la température…`,
        `Reste égale à ${T}${NB}°C`,
        [
          [
            'Augmente lentement',
            'Pour un corps pur, la température reste constante pendant le changement d’état : c’est le palier.',
          ],
          [
            'Diminue',
            'On chauffe : la température ne baisse pas. Pendant le changement d’état, elle reste constante.',
          ],
          [
            `Reste égale à ${other}${NB}°C`,
            `${other}${NB}°C, c’est la température ${other === 0 ? 'de fusion' : 'd’ébullition'} de l’eau, pas celle de ${what}.`,
          ],
        ],
        `Palier : la température de l’eau pure reste à ${T}${NB}°C pendant toute ${what}.`,
        rng,
      );
    }
    case 'mass-conserved': {
      const m = pick([50, 120, 250], rng);
      return q(
        kind,
        `On fait fondre complètement ${m}${NB}g de glace. Quelle est la masse d’eau liquide obtenue ?`,
        g(m, 0),
        [
          [
            g(Math.round(m * 0.92), 0),
            'C’est le volume qui change (la glace prend plus de place), pas la masse.',
          ],
          [g(Math.round(m * 1.09), 0), 'La masse se conserve lors d’un changement d’état.'],
          [g(m / 2, 0), 'Toute la glace a fondu : rien n’a disparu.'],
        ],
        `Lors d’un changement d’état, la masse se conserve : ${m}${NB}g de glace donnent ${m}${NB}g d’eau liquide.`,
        rng,
      );
    }
    case 'same-volume': {
      const ms = shuffle(MATERIALS, rng).slice(0, 4);
      const best = ms.reduce((a, b) => (b.rho > a.rho ? b : a));
      return q(
        kind,
        `Quatre cubes de même volume : ${ms.map((x) => x.name).join(', ')}. Lequel a la plus grande masse ?`,
        best.label,
        ms
          .filter((x) => x !== best)
          .map(
            (x) =>
              [
                x.label,
                `À volume égal, ${x.the} a une masse plus petite que ${best.the}.`,
              ] as const,
          ),
        `À volume égal, la masse dépend du matériau : ${best.the}, ${fr(best.rho, 2)}${NB}g par cm³, l’emporte.`,
        rng,
      );
    }
    case 'dissolve-mass': {
      const s = pick(['sel', 'sucre'] as const, rng);
      const m = pick([10, 20, 30], rng);
      const w = pick([150, 200, 250], rng);
      return q(
        kind,
        `On dissout complètement ${m}${NB}g de ${s} dans ${w}${NB}g d’eau. Quelle est la masse de la solution ?`,
        g(w + m, 0),
        [
          [g(w, 0), `Le ${s} dissous est toujours là, même invisible : sa masse compte.`],
          [g(w - m, 0), 'On ajoute une masse, on n’en retire pas.'],
          [g(m, 0), `${m}${NB}g, c’est seulement la masse de ${s}.`],
        ],
        `La masse se conserve lors d’une dissolution : ${w} + ${m} = ${w + m}${NB}g.`,
        rng,
      );
    }
    case 'particles': {
      const p = pick(PARTICLE_STATES, rng);
      return q(
        kind,
        `Dans ${p.state}, les particules sont…`,
        p.text,
        [
          ...PARTICLE_STATES.filter((x) => x !== p).map(
            (x) => [x.text, `Cela décrit ${x.state}.`] as const,
          ),
          ['Immobiles et éloignées', 'Les particules sont toujours en mouvement.'],
        ],
        `Modèle particulaire : ${p.state}, particules ${p.text.toLowerCase()}.`,
        rng,
      );
    }
    case 'pure-mixture': {
      const plateau = rng() < 0.5;
      return q(
        kind,
        plateau
          ? 'Pendant la solidification d’un liquide, la température reste constante à 6,5 °C. Ce liquide est…'
          : 'Pendant la solidification d’un liquide, la température continue de baisser, sans palier. Ce liquide est…',
        plateau ? 'Un corps pur' : 'Un mélange',
        [
          plateau
            ? [
                'Un mélange',
                'Un mélange n’a pas de palier : sa température change pendant le changement d’état.',
              ]
            : [
                'Un corps pur',
                'Un corps pur présente un palier : température constante pendant le changement d’état.',
              ],
          plateau
            ? ['De l’eau pure', 'L’eau pure se solidifie à 0 °C, pas à 6,5 °C.']
            : ['De l’eau pure', 'L’eau pure est un corps pur : elle a un palier à 0 °C.'],
          ['Impossible à dire', 'La présence ou non d’un palier permet de conclure.'],
        ],
        'Palier (température constante) : corps pur ; pas de palier : mélange.',
        rng,
      );
    }
    case 'volume-change':
      return q(
        kind,
        'Une bouteille pleine d’eau oubliée au congélateur éclate. Pourquoi ?',
        'En gelant, l’eau occupe plus de volume (sa masse ne change pas)',
        [
          [
            'En gelant, la masse de l’eau augmente',
            'La masse se conserve lors d’un changement d’état.',
          ],
          [
            'En gelant, le volume de l’eau diminue',
            'Au contraire : la glace occupe environ 9 % de volume en plus.',
          ],
          [
            'La glace est plus dense que l’eau liquide',
            'La glace flotte : elle est moins dense que l’eau liquide.',
          ],
        ],
        'L’eau est une exception : en se solidifiant, son volume augmente d’environ 9 % ; la masse, elle, se conserve.',
        rng,
      );
    case 'saturation': {
      const x = pick([40, 50, 60], rng);
      return q(
        kind,
        `La solubilité du sel est de 36${NB}g pour 100${NB}mL d’eau. On ajoute ${x}${NB}g de sel à 100${NB}mL d’eau et on agite longtemps. Quelle masse de sel reste au fond ?`,
        g(x - 36, 0),
        [
          [g(x, 0), 'Une partie se dissout : jusqu’à 36 g.'],
          [g(36, 0), '36 g, c’est ce qui se dissout, pas ce qui reste au fond.'],
          [g(0, 0), 'Au-delà de 36 g, la solution est saturée : le reste ne se dissout pas.'],
        ],
        `36${NB}g se dissolvent (solution saturée) ; ${x} − 36 = ${x - 36}${NB}g restent au fond.`,
        rng,
      );
    }
    case 'rho-calc': {
      const mat = pick(
        MATERIALS.filter((x) => x.rho >= 0.7),
        rng,
      );
      const V = pick([10, 20, 25, 50], rng);
      const m = mat.rho * V;
      return q(
        kind,
        `Un objet a une masse m = ${fr(m, 1)}${NB}g et un volume V = ${V}${NB}cm³. Quelle est sa masse volumique ?`,
        rhoText(mat.rho),
        [
          [rhoText(V / m), 'ρ = m ÷ V, pas V ÷ m.'],
          [`${fr(m * V, 0)}${NB}g/cm³`, 'On divise : ρ = m ÷ V.'],
          [
            `${fr(mat.rho, 2)}${NB}kg/m³`,
            `Unités : ${fr(mat.rho, 2)} g/cm³ = ${fr(mat.rho * 1000, 0)} kg/m³.`,
          ],
        ],
        `ρ = m ÷ V = ${fr(m, 1)} ÷ ${V} = ${fr(mat.rho, 2)}${NB}g/cm³.`,
        rng,
      );
    }
    case 'mass-calc': {
      const air = rng() < 0.4;
      if (air) {
        const V = pick([30, 50, 75], rng);
        return q(
          kind,
          `Une salle de ${V}${NB}m³ est remplie d’air (ρ = 1,2${NB}kg/m³). Quelle est la masse de cet air ?`,
          `${fr(1.2 * V, 0)}${NB}kg`,
          [
            [`${fr(V / 1.2, 1)}${NB}kg`, 'm = ρ × V, pas V ÷ ρ.'],
            [`1,2${NB}kg`, 'C’est la masse d’UN mètre cube d’air.'],
            ['0 kg', 'L’air a une masse : 1,2 kg par mètre cube.'],
          ],
          `m = ρ × V = 1,2 × ${V} = ${fr(1.2 * V, 0)}${NB}kg : l’air est pesant !`,
          rng,
        );
      }
      const V = pick([2, 3, 5], rng);
      const liq = pick(
        LIQUIDS.filter((x) => x.id !== 'water'),
        rng,
      );
      return q(
        kind,
        `Quelle est la masse de ${V}${NB}L ${de(liq.name)} (ρ = ${fr(liq.rho, 2)}${NB}kg/L) ?`,
        `${fr(liq.rho * V, 2)}${NB}kg`,
        [
          [`${fr(V / liq.rho, 2)}${NB}kg`, 'm = ρ × V, pas V ÷ ρ.'],
          [`${fr(liq.rho, 2)}${NB}kg`, 'C’est la masse d’UN litre.'],
          [`${V}${NB}kg`, `${V} kg, ce serait pour de l’eau (ρ = 1 kg/L).`],
        ],
        `m = ρ × V = ${fr(liq.rho, 2)} × ${V} = ${fr(liq.rho * V, 2)}${NB}kg.`,
        rng,
      );
    }
    case 'units': {
      const v = pick([0.79, 1.2, 2.7, 0.92], rng);
      const toKg = rng() < 0.5;
      const unit = toKg ? 'kg/m³' : 'g/L';
      return q(
        kind,
        `${fr(v, 2)}${NB}g/cm³ = ? ${unit}`,
        `${fr(v * 1000, 0)}${NB}${unit}`,
        [
          [
            `${fr(v, 2)}${NB}${unit}`,
            'Les unités changent, donc la valeur aussi : 1 g/cm³ = 1 000 ' + unit + '.',
          ],
          [`${fr(v * 10, 1)}${NB}${unit}`, `1 g/cm³ = 1 000 ${unit}, pas 10.`],
          [`${fr(v / 1000, 5)}${NB}${unit}`, 'Dans ce sens, on multiplie par 1 000.'],
        ],
        `1${NB}g/cm³ = 1${NB}000${NB}${unit} (1 L = 1 000 cm³ ; 1 kg = 1 000 g et 1 m³ = 1 000 000 cm³) : ${fr(v, 2)}${NB}g/cm³ = ${fr(v * 1000, 0)}${NB}${unit}.`,
        rng,
      );
    }
    case 'floats': {
      const liq = pick(LIQUIDS, rng);
      const mat = pick(
        MATERIALS.filter((x) => Math.abs(x.rho - liq.rho) > 0.05 && x.rho < 3),
        rng,
      );
      const fl = mat.rho < liq.rho;
      return q(
        kind,
        `Un cube ${de(mat.name)} (ρ = ${fr(mat.rho, 2)}${NB}g/cm³) est posé dans de ${liq.the} (ρ = ${fr(liq.rho, 2)}${NB}g/cm³). Que se passe-t-il ?`,
        fl ? 'Il flotte' : 'Il coule',
        [
          fl
            ? ['Il coule', 'ρ(objet) < ρ(liquide) : il flotte.']
            : ['Il flotte', 'ρ(objet) > ρ(liquide) : il coule.'],
          ['Il reste entre deux eaux', 'Seulement si les masses volumiques sont égales.'],
          [
            'Cela dépend de sa masse',
            'C’est la masse volumique qui compte, pas la masse : un gros cube et un petit font pareil.',
          ],
        ],
        `On compare les masses volumiques : ${fr(mat.rho, 2)} ${fl ? '<' : '>'} ${fr(liq.rho, 2)}, le cube ${fl ? 'flotte' : 'coule'}.`,
        rng,
      );
    }
    case 'identify': {
      const metals = MATERIALS.filter((x) => ['alu', 'iron', 'copper', 'gold'].includes(x.id));
      const mat = pick(metals, rng);
      const V = pick([5, 10, 20], rng);
      const m = mat.rho * V;
      return q(
        kind,
        `Un objet métallique : m = ${fr(m, 1)}${NB}g, V = ${V}${NB}cm³. Masses volumiques : aluminium 2,7 ; fer 7,87 ; cuivre 8,96 ; or 19,3 (g/cm³). Quel est ce métal ?`,
        mat.label,
        metals
          .filter((x) => x !== mat)
          .map(
            (x) => [x.label, `Calcule ρ = m ÷ V = ${fr(mat.rho, 2)} g/cm³, puis compare.`] as const,
          ),
        `ρ = m ÷ V = ${fr(m, 1)} ÷ ${V} = ${fr(mat.rho, 2)}${NB}g/cm³ : c’est ${mat.the}.`,
        rng,
      );
    }
    case 'latent-heat': {
      const vap = rng() < 0.4;
      const m = vap ? pick([20, 50], rng) : pick([50, 200, 250], rng);
      const L = vap ? 2257 : 334;
      return q(
        kind,
        vap
          ? `Quelle énergie faut-il pour vaporiser ${m}${NB}g d’eau à 100${NB}°C (L_vap = 2${NB}257${NB}J/g) ?`
          : `Quelle énergie faut-il pour faire fondre ${m}${NB}g de glace à 0${NB}°C (L_fus = 334${NB}J/g) ?`,
        `${fr(m * L, 0)}${NB}J`,
        [
          [`${fr(L, 0)}${NB}J`, `C’est l’énergie pour 1 g seulement : on multiplie par ${m}.`],
          [`${fr(L / m, 2)}${NB}J`, 'Q = m × L, pas L ÷ m.'],
          [`${fr(m + L, 0)}${NB}J`, 'On multiplie : Q = m × L.'],
        ],
        `Q = m × L = ${m}${NB}g × ${fr(L, 0)}${NB}J/g = ${fr(m * L, 0)}${NB}J.`,
        rng,
      );
    }
    case 'endo-exo': {
      const c = pick(CHANGES, rng);
      const endo = c.name === 'La fusion' || c.name === 'La vaporisation';
      return q(
        kind,
        `${c.name} (état ${c.from} → état ${c.to}) est une transformation…`,
        endo
          ? 'Endothermique : le système reçoit de l’énergie'
          : 'Exothermique : le système cède de l’énergie',
        [
          endo
            ? [
                'Exothermique : le système cède de l’énergie',
                'Pour passer de l’état ordonné à l’état désordonné, il faut fournir de l’énergie.',
              ]
            : [
                'Endothermique : le système reçoit de l’énergie',
                'Vers un état plus ordonné, le système cède de l’énergie au milieu extérieur.',
              ],
          [
            'Ni l’un ni l’autre : la température reste constante',
            'Température constante, mais énergie échangée : Q = m × L.',
          ],
          endo
            ? [
                'Endothermique : le système cède de l’énergie',
                'Endothermique, c’est recevoir de l’énergie.',
              ]
            : [
                'Exothermique : le système reçoit de l’énergie',
                'Exothermique, c’est céder de l’énergie.',
              ],
        ],
        `${c.name} est ${endo ? 'endothermique : il faut chauffer' : 'exothermique : le système chauffe son entourage'}.`,
        rng,
      );
    }
    case 'identify-temp': {
      const list = [
        ['La glace', 0],
        ['Le cyclohexane', 6.5],
        ['L’acide stéarique', 69],
        ['Le naphtalène', 80],
      ] as const;
      const [name, T] = pick(list, rng);
      return q(
        kind,
        `Températures de fusion : glace 0 °C, cyclohexane 6,5 °C, acide stéarique 69 °C, naphtalène 80 °C. Un solide blanc fond avec un palier à ${fr(T, 1)}${NB}°C. Lequel est-ce ?`,
        name,
        list
          .filter(([n]) => n !== name)
          .map(([n, t]) => [n, `${n} fond à ${fr(t, 1)} °C.`] as const),
        `La température de fusion (palier) identifie l’espèce : ${fr(T, 1)}${NB}°C, c’est ${name.toLowerCase()}.`,
        rng,
      );
    }
    case 'mass-concentration': {
      const m = pick([5, 10, 2], rng);
      const V = pick([250, 500, 200], rng);
      const c = m / (V / 1000);
      return q(
        kind,
        `On dissout ${m}${NB}g de sel et on complète à ${V}${NB}mL de solution. Concentration en masse C_m ?`,
        `${fr(c, 1)}${NB}g/L`,
        [
          [`${fr(m / V, 3)}${NB}g/L`, `Convertis d’abord : ${V} mL = ${fr(V / 1000, 3)} L.`],
          [`${fr(V / m, 1)}${NB}g/L`, 'C_m = m ÷ V, pas V ÷ m.'],
          [`${fr(m * (V / 1000), 2)}${NB}g/L`, 'On divise la masse par le volume.'],
        ],
        `C_m = m ÷ V = ${m}${NB}g ÷ ${fr(V / 1000, 3)}${NB}L = ${fr(c, 1)}${NB}g/L.`,
        rng,
      );
    }
  }
}
